import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Upload, Download, Search, AlertCircle, X, HandCoins, ChevronRight, ChevronDown, Filter, FileText, FileSpreadsheet, Columns3, Send, Ban, Copy } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { saveBlob } from "@/components/payroll/bankUtils";
import { isArastirmaPozisyon, isHacizBirimiPozisyon, isCallCenterPozisyon } from "@/lib/pozisyonColors";
import SavedFilterPresets from "@/components/SavedFilterPresets";
import QuickDateFilters from "@/components/QuickDateFilters";
import { ImportPreviewModal } from "@/components/tahsilat/ImportPreviewModal";
import { DashboardVisibilityControl } from "@/components/tahsilat/DashboardVisibilityControl";
import { GonderimOzetPanel } from "@/components/tahsilat/GonderimOzetPanel";
import { SummaryFlagsControl } from "@/components/tahsilat/SummaryFlagsControl";
import { DosyaDurumuOzet } from "@/components/tahsilat/DosyaDurumuOzet";
import { YesterdayModal } from "@/components/tahsilat/YesterdayModal";
import { TemplateInput } from "@/components/tahsilat/TemplateInput";
import { DosyaDurumuBadge, DosyaDurumuFilter } from "@/components/tahsilat/DosyaDurumu";
import { BulkDosyaDurumu } from "@/components/tahsilat/BulkDosyaDurumu";

const TEMPLATE_FIELDS = new Set(["icra_mudurlugu", "geldigi_banka", "kimin_hesabina_gelmis", "dosya_durumu"]);
import { useAuth } from "@/lib/auth";
import { DateInput } from "@/components/DateInput";
import { SortTh, useColumnSort } from "@/components/SortTh";
import { LoadError, LoadingRow } from "@/components/LoadingState";

const TAHSILAT_NUM_KEYS = ["tahsilat_miktari", "vekalet_ucreti"];

const FIELD_GROUPS = [
  {
    title: "Müvekkil / Dosya",
    fields: [
      { k: "musteri_no", l: "Müşteri Numarası" },
      { k: "islem_no", l: "İşlem No" },
      { k: "cek_mikro_no", l: "Çek/Mikro No" },
      { k: "icra_foy", l: "İcra Föy", important: true },
      { k: "icra_mudurlugu", l: "İcra Müdürlüğü" },
      { k: "dosya_no", l: "Dosya No", important: true },
      { k: "dosya_durumu", l: "Dosya Durumu" },
      // muvekkil is handled specially (dropdown) — excluded here
      { k: "musteri", l: "Müşteri" },
      { k: "kesideci", l: "Keşideci" },
      { k: "odemeyi_yapan_taraf", l: "Ödemeyi Yapan Taraf" },
      { k: "odeyen_turu", l: "Ödeyen Türü", type: "select", options: ["", "Müşteri", "Kefil", "Keşideci", "Fatura Borçlusu", "Ciranta", "3. Şahıs"] },
    ],
  },
  {
    title: "Tutarlar",
    fields: [
      { k: "vade", l: "Vade", type: "date" },
      { k: "tahsilat_miktari", l: "Tahsilat Miktarı (₺)", type: "money" },
      { k: "tahsilat_tarihi", l: "Tahsilat Tarihi", type: "date" },
      { k: "vekalet_ucreti", l: "Vekalet Ücreti (₺)", type: "money" },
      { k: "ana_para", l: "Ana Para (₺)", type: "money" },
      { k: "faiz", l: "Faiz (₺)", type: "money" },
      { k: "masraf_avans", l: "Masraf (Avans) (₺)", type: "money" },
      // toplam_gonderilecek handled specially (auto-calc readonly)
      { k: "toplam_takip_tutari", l: "Toplam Takip Tutarı (₺)", type: "money" },
    ],
  },
  {
    title: "Gönderme & Banka",
    fields: [
      { k: "gonderme_tarihi", l: "Gönderme Tarihi", type: "date" },
      { k: "kimin_hesabina_gelmis", l: "Kimin Hesabına Gelmiş" },
      { k: "geldigi_banka", l: "Geldiği Banka" },
    ],
  },
  {
    title: "İadeler & Teminatlar",
    fields: [
      { k: "masraf_avans_iade", l: "Masraf (Avans) İade (₺)", type: "money" },
      { k: "cek_tazminati", l: "Çek Tazminatı (₺)", type: "money" },
      { k: "nakit_teminat_iade", l: "Nakit Teminat İade (₺)", type: "money" },
      { k: "yediemin_masraf", l: "Yediemin Masraf (₺)", type: "money" },
      { k: "indirim_onayi", l: "İndirim Onayı" },
      { k: "istihkak", l: "İstihkak", type: "select", options: ["", "Var", "Yok"] },
    ],
  },
];

// Universal column catalogue for filtering & export selection.
// Mirrors backend EXCEL_FIELD_KEYS / NUMBER_FIELDS / DATE_FIELDS in `tahsilat.py`.
const COLUMN_META = [
  { k: "musteri_no",         l: "Müşteri Numarası",          t: "text" },
  { k: "islem_no",           l: "İşlem No",                   t: "text" },
  { k: "cek_mikro_no",       l: "Çek / Mikro No",             t: "text" },
  { k: "icra_foy",           l: "İcra Föy",                   t: "text" },
  { k: "icra_mudurlugu",     l: "İcra Müdürlüğü",             t: "text" },
  { k: "dosya_durumu",       l: "D. Durumu",                  t: "text" },
  { k: "dosya_durumu_tarihi", l: "D. Durumu Tarihi",          t: "date" },
  { k: "dosya_no",           l: "Dosya No",                   t: "text" },
  { k: "muvekkil",           l: "Müvekkil",                   t: "text" },
  { k: "musteri",            l: "Müşteri",                    t: "text" },
  { k: "kesideci",           l: "Keşideci",                   t: "text" },
  { k: "odemeyi_yapan_taraf",l: "Ödemeyi Yapan Taraf",        t: "text" },
  { k: "odeyen_turu",        l: "Ödeyen Türü",                t: "text" },
  { k: "vade",               l: "Vade",                       t: "date" },
  { k: "tahsilat_miktari",   l: "Tahsilat Miktarı (₺)",       t: "number" },
  { k: "tahsilat_tarihi",    l: "Tahsilat Tarihi",            t: "date" },
  { k: "vekalet_ucreti",     l: "Vekalet Ücreti (₺)",         t: "number" },
  { k: "ana_para",           l: "Ana Para (₺)",               t: "number" },
  { k: "faiz",               l: "Faiz (₺)",                   t: "number" },
  { k: "masraf_avans",       l: "Masraf (Avans) (₺)",         t: "number" },
  { k: "toplam_gonderilecek",l: "Toplam Gönderilecek (₺)",    t: "number" },
  { k: "gonderme_tarihi",    l: "Gönderme Tarihi",            t: "date" },
  { k: "kimin_hesabina_gelmis", l: "Kimin Hesabına Gelmiş",   t: "text" },
  { k: "geldigi_banka",      l: "Geldiği Banka",              t: "text" },
  { k: "arastiran_text",     l: "Araştıran",                  t: "text" },
  { k: "gidenler_text",      l: "Hacze Gidenler",             t: "text" },
  { k: "toplam_takip_tutari",l: "Toplam Takip Tutarı (₺)",    t: "number" },
  { k: "masraf_avans_iade",  l: "Masraf (Avans) İade (₺)",    t: "number" },
  { k: "cek_tazminati",      l: "Çek Tazminatı (₺)",          t: "number" },
  { k: "nakit_teminat_iade", l: "Nakit Teminat İade (₺)",     t: "number" },
  { k: "yediemin_masraf",    l: "Yediemin Masraf (₺)",        t: "number" },
  { k: "indirim_onayi",      l: "İndirim Onayı",              t: "text" },
  { k: "istihkak",           l: "İstihkak",                   t: "text" },
];
const COLUMN_META_BY_KEY = Object.fromEntries(COLUMN_META.map((c) => [c.k, c]));

/** Serialise colFilters state to a compact JSON string the backend understands. */
function serializeColFilters(colFilters) {
  const out = {};
  for (const [k, c] of Object.entries(colFilters || {})) {
    if (!c) continue;
    const meta = COLUMN_META_BY_KEY[k];
    if (!meta) continue;
    if (meta.t === "text") {
      if ((c.value || "").trim()) out[k] = { value: c.value.trim() };
    } else {
      const o = {};
      if (c.min !== undefined && c.min !== "") o.min = c.min;
      if (c.max !== undefined && c.max !== "") o.max = c.max;
      if (Object.keys(o).length) out[k] = o;
    }
  }
  return Object.keys(out).length ? JSON.stringify(out) : "";
}

function countColFilters(colFilters) {
  return Object.values(colFilters || {}).filter((c) => {
    if (!c) return false;
    if ((c.value || "").toString().trim()) return true;
    if (c.min !== undefined && c.min !== "") return true;
    if (c.max !== undefined && c.max !== "") return true;
    return false;
  }).length;
}

export default function TahsilatTab() {
  const [rows, setRows] = useState([]);
  const colSort = useColumnSort(rows, TAHSILAT_NUM_KEYS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [copyFrom, setCopyFrom] = useState(null);
  const [personnel, setPersonnel] = useState([]);
  const [creditors, setCreditors] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showExport, setShowExport] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  // Filters
  const [filters, setFilters] = useState({ from_date: "", to_date: "", muvekkil: "", personnel_id: "", from_gonderme: "", to_gonderme: "" });
  const [colFilters, setColFilters] = useState({}); // { field: {value?, min?, max?} }
  const [addColKey, setAddColKey] = useState(""); // controlled "add column" dropdown
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hideNotSending, setHideNotSending] = useState(false);
  const { isAdmin, user, canManageStatements } = useAuth();
  const [ozetKey, setOzetKey] = useState(0);
  const [yesterdayOpen, setYesterdayOpen] = useState(false);
  const [filterTick, setFilterTick] = useState(0);
  useEffect(() => { if (filterTick) load(); /* eslint-disable-next-line */ }, [filterTick]);
  const applyOzetFilter = ({ muvekkil, from, to, label }) => {
    setSearch("");
    setColFilters({});
    setFilters({ from_date: "", to_date: "", muvekkil, personnel_id: "", from_gonderme: from, to_gonderme: to });
    setHideNotSending(true);
    setFiltersOpen(true);
    setFilterTick((t) => t + 1);
    toast.info(`${muvekkil} · ${label}`);
    setTimeout(() => document.querySelector("[data-testid=tahsilat-search]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  };
  const applyDurumFilter = ({ muvekkil, from, to, durum, label }) => {
    setSearch("");
    setColFilters(durum ? { dosya_durumu: { value: durum } } : {});
    setFilters({ from_date: from, to_date: to, muvekkil: muvekkil === "—" ? "" : muvekkil, personnel_id: "", from_gonderme: "", to_gonderme: "" });
    setHideNotSending(false);
    setFiltersOpen(true);
    setFilterTick((t) => t + 1);
    toast.info(`${muvekkil} · ${label}`);
    setTimeout(() => document.querySelector("[data-testid=tahsilat-search]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  };

  const buildQuery = () => {
    const p = new URLSearchParams();
    if (search) p.set("q", search);
    if (filters.from_date) p.set("from_date", filters.from_date);
    if (filters.to_date) p.set("to_date", filters.to_date);
    if (filters.from_gonderme) p.set("from_gonderme", filters.from_gonderme);
    if (filters.to_gonderme) p.set("to_gonderme", filters.to_gonderme);
    if (filters.muvekkil) p.set("muvekkil", filters.muvekkil);
    if (filters.personnel_id) p.set("personnel_id", filters.personnel_id);
    if (hideNotSending) p.set("hide_not_sending", "true");
    const cf = serializeColFilters(colFilters);
    if (cf) p.set("cf", cf);
    const qs = p.toString();
    return qs ? `?${qs}` : "";
  };

  const load = async () => {
    setLoading(true);
    try {
      const [t, p, dash, cr] = await Promise.all([
        api.get(`/tahsilat${buildQuery()}`),
        personnel.length ? Promise.resolve({ data: personnel }) : api.get("/personnel"),
        api.get("/tahsilat/dashboard"),
        creditors.length ? Promise.resolve({ data: { rows: creditors } }) : api.get("/creditors"),
      ]);
      setRows(t.data?.rows || []);
      if (!personnel.length) setPersonnel((p.data || []).filter((x) => x.aktif !== false));
      if (!creditors.length) setCreditors(cr.data?.rows || []);
      setDashboard(dash.data || null);
      setOzetKey((k) => k + 1);
      setSelectedIds(new Set());
      setLoadError(false);
    } catch {
      setLoadError(true);
      toast.error("Tahsilatlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Highlight target row driven by deep-link events (from Admin notification inbox)
  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      const tid = e?.detail;
      if (!tid) return;
      // Ensure the row isn't filtered out
      setHideNotSending(false);
      setHighlightId(tid);
      // Trigger a reload only if the row isn't already in current rows
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="tahsilat-row-${tid}"]`);
        if (el) {
          try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
        } else {
          // Row not loaded yet — try reloading then scroll
          load();
          setTimeout(() => {
            const el2 = document.querySelector(`[data-testid="tahsilat-row-${tid}"]`);
            if (el2) { try { el2.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {} }
          }, 800);
        }
      }, 150);
      // Clear highlight after 4 seconds
      setTimeout(() => setHighlightId((cur) => (cur === tid ? null : cur)), 4000);
    };
    window.addEventListener("bordromat:tahsilat-highlight", handler);
    // Also check sessionStorage on mount (set by notification click)
    try {
      const pending = sessionStorage.getItem("bordromat:tahsilat-highlight-pending");
      if (pending) {
        sessionStorage.removeItem("bordromat:tahsilat-highlight-pending");
        handler({ detail: pending });
      }
    } catch {}
    return () => window.removeEventListener("bordromat:tahsilat-highlight", handler);
    // eslint-disable-next-line
  }, []);

  const clearFilters = () => {
    setFilters({ from_date: "", to_date: "", muvekkil: "", personnel_id: "", from_gonderme: "", to_gonderme: "" });
    setColFilters({});
    setSearch("");
    setHideNotSending(false);
    setTimeout(load, 50);
  };
  const activeFilterCount =
    (filters.from_date ? 1 : 0) + (filters.to_date ? 1 : 0) +
    (filters.from_gonderme ? 1 : 0) + (filters.to_gonderme ? 1 : 0) +
    (filters.muvekkil ? 1 : 0) + (filters.personnel_id ? 1 : 0) +
    (hideNotSending ? 1 : 0) +
    countColFilters(colFilters);

  const persById = useMemo(() => Object.fromEntries(personnel.map((p) => [p.id, p])), [personnel]);

  const toggleRowSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const toggleAll = () => {
    if (selectedIds.size === rows.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map((r) => r.id)));
  };
  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < rows.length;

  const handleDelete = async (id) => {
    if (!window.confirm("Tahsilat silinecek. Onaylıyor musunuz?")) return;
    try {
      await api.delete(`/tahsilat/${id}`);
      toast.success("Silindi");
      load();
    } catch {
      toast.error("Silinemedi");
    }
  };

  const toggleGonderimDurumu = async (row) => {
    const current = (row.gonderim_durumu || "gonderilecek").toLowerCase();
    const next = current === "gonderilmeyecek" ? "gonderilecek" : "gonderilmeyecek";
    // Optimistic update
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, gonderim_durumu: next } : r)));
    try {
      await api.patch(`/tahsilat/${row.id}/gonderim-durumu`, { gonderim_durumu: next });
      toast.success(next === "gonderilmeyecek" ? "Gönderilmeyecek olarak işaretlendi" : "Gönderilecek olarak işaretlendi");
    } catch {
      // Revert on failure
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, gonderim_durumu: current } : r)));
      toast.error("Durum güncellenemedi");
    }
  };

  const bulkSetGonderimDurumu = async (val) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const label = val === "gonderilmeyecek" ? "Gönderilmeyecek" : "Gönderilecek";
    if (!window.confirm(`${ids.length} satır "${label}" olarak işaretlenecek. Onaylıyor musunuz?`)) return;
    // Snapshot previous statuses so we can undo
    const prevById = new Map();
    rows.forEach((r) => {
      if (ids.includes(r.id)) prevById.set(r.id, (r.gonderim_durumu || "gonderilecek").toLowerCase());
    });
    try {
      const r = await api.post("/tahsilat/bulk-gonderim-durumu", { ids, gonderim_durumu: val });
      load();
      toast.success(`${r.data.updated} satır "${label}" olarak işaretlendi`, {
        duration: 8000,
        action: {
          label: "Geri Al",
          onClick: async () => {
            // Group ids by their previous value and send one bulk request per group
            const groups = { gonderilecek: [], gonderilmeyecek: [] };
            prevById.forEach((prev, id) => {
              if (prev === "gonderilmeyecek") groups.gonderilmeyecek.push(id);
              else groups.gonderilecek.push(id);
            });
            try {
              const calls = [];
              if (groups.gonderilecek.length) {
                calls.push(api.post("/tahsilat/bulk-gonderim-durumu", { ids: groups.gonderilecek, gonderim_durumu: "gonderilecek" }));
              }
              if (groups.gonderilmeyecek.length) {
                calls.push(api.post("/tahsilat/bulk-gonderim-durumu", { ids: groups.gonderilmeyecek, gonderim_durumu: "gonderilmeyecek" }));
              }
              await Promise.all(calls);
              toast.success(`${ids.length} satır eski durumuna döndürüldü`);
              load();
            } catch {
              toast.error("Geri alınamadı");
            }
          },
        },
      });
    } catch {
      toast.error("Toplu güncelleme başarısız");
    }
  };

  // Row color by priority: durum > tarih
  const todayISO = new Date().toISOString().slice(0, 10);
  const rowStatusClass = (r) => {
    const gd = (r.gonderim_durumu || "gonderilecek").toLowerCase();
    if (gd === "gonderilmeyecek") return "bg-yellow-100 hover:bg-yellow-200";
    const gt = (r.gonderme_tarihi || "").trim();
    if (!gt) return "";
    if (gt === todayISO) return "bg-orange-100 hover:bg-orange-200";
    if (gt < todayISO) return "bg-rose-100 hover:bg-rose-200";
    return "";
  };

  const openCreate = () => { setEditing(null); setCopyFrom(null); setShowModal(true); };
  const openEdit = (row) => { setEditing(row); setCopyFrom(null); setShowModal(true); };
  const openCopy = (row) => {
    if (user && localStorage.getItem(`tahsilat_draft_${user.id || user.email}`)
      && !window.confirm("Kaydedilmemiş bir Yeni Tahsilat taslağınız var. Kopyalama bu taslağın yerini alacak. Devam edilsin mi?")) return;
    setEditing(null); setCopyFrom(row); setShowModal(true);
  };

  const handleImportTemplate = () => api.get("/tahsilat/import/template", { responseType: "blob" })
    .then((r) => saveBlob(r, "tahsilat_sablon.xlsx")).catch(() => toast.error("Şablon indirilemedi"));

  const [importPreview, setImportPreview] = useState(null);

  const runImport = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/tahsilat/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.inserted} kayıt içe aktarıldı`);
      if (r.data.warnings?.length) toast.warning(`${r.data.warnings.length} uzun isim 1000 karaktere kısaltıldı`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} hata`);
      load();
    } catch {
      toast.error("İçe aktarım başarısız");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/tahsilat/import?dry_run=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!r.data.total_rows) { toast.error("Dosyada aktarılacak satır bulunamadı"); return; }
      setImportPreview({ file, ...r.data });
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "İçe aktarım başarısız");
    }
  };

  const fmtTL = (v) => (v == null || v === "") ? "-" : Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-4">
      {/* Dashboard counters */}
      {(isAdmin || canManageStatements) && (
        <div className="flex justify-end gap-2">
          <DashboardVisibilityControl kind="dosya_durumu" title="Dosya Durumu Özeti" label="Dosya Durumu Görünürlüğü" choices={["muhasebeci", "muhasebe_stj"]} onChanged={() => setOzetKey((k) => k + 1)} />
          {isAdmin && <>
          <DashboardVisibilityControl kind="gonderim" title="Müvekkil bazında gönderilecek tutarlar" label="Gönderim Özeti Görünürlüğü" onChanged={() => setOzetKey((k) => k + 1)} />
          <SummaryFlagsControl onChanged={load} />
          </>}
        </div>
      )}
      {yesterdayOpen && dashboard?.yesterday_date && (
        <YesterdayModal date={dashboard.yesterday_date} fmt={fmtTL} showT={dashboard.show_tahsilat !== false} showV={dashboard.show_vekalet !== false}
          onClose={() => setYesterdayOpen(false)}
          onFilter={() => { setYesterdayOpen(false); applyDurumFilter({ muvekkil: "", from: dashboard.yesterday_date, to: dashboard.yesterday_date, durum: null, label: "Dünkü tahsilatlar" }); }} />
      )}
      <GonderimOzetPanel reloadKey={ozetKey} onSelect={applyOzetFilter} onEditRow={openEdit} onDurumSelect={applyDurumFilter} />
      <DosyaDurumuOzet reloadKey={ozetKey} onSelect={applyDurumFilter} />
      {dashboard && (
        <div className="space-y-2" data-testid="tahsilat-dashboard">
          {!dashboard.hidden && (
          <div className={`grid grid-cols-2 ${dashboard.all ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3`} data-testid="tahsilat-dashboard-cards">
            <DashCard title="Bu Hafta" count={dashboard.week.count} tahsilat={dashboard.week.tahsilat_miktari} vekalet={dashboard.week.vekalet_ucreti} fmt={fmtTL} tone="emerald" showT={dashboard.show_tahsilat !== false} showV={dashboard.show_vekalet !== false} />
            <DashCard title="Bu Ay" count={dashboard.month.count} tahsilat={dashboard.month.tahsilat_miktari} vekalet={dashboard.month.vekalet_ucreti} fmt={fmtTL} tone="indigo" showT={dashboard.show_tahsilat !== false} showV={dashboard.show_vekalet !== false} />
            <DashCard onClick={() => setYesterdayOpen(true)} testid="dash-card-yesterday" title={`Dün · ${(dashboard.yesterday_date || "").split("-").reverse().join(".")}`} count={dashboard.yesterday.count} tahsilat={dashboard.yesterday.tahsilat_miktari} vekalet={dashboard.yesterday.vekalet_ucreti} fmt={fmtTL} tone="sky" showT={dashboard.show_tahsilat !== false} showV={dashboard.show_vekalet !== false} />
            {dashboard.all && <DashCard title="Toplam" count={dashboard.all.count} tahsilat={dashboard.all.tahsilat_miktari} vekalet={dashboard.all.vekalet_ucreti} fmt={fmtTL} tone="slate" showT={dashboard.show_tahsilat !== false} showV={dashboard.show_vekalet !== false} />}
          </div>
          )}
          {dashboard.not_sending_count > 0 && (
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => { setHideNotSending(false); setFiltersOpen(true); setTimeout(load, 50); }}
                data-testid="tahsilat-not-sending-badge"
                title="Gönderilmeyecek işaretli kayıtları göster"
                className="inline-flex items-center gap-1.5 bg-yellow-100 hover:bg-yellow-200 border border-yellow-300 text-yellow-900 text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors"
              >
                <Ban size={12} />
                Gönderilmeyecek: {dashboard.not_sending_count} kayıt
              </button>
            </div>
          )}
        </div>
      )}

      <div className="bg-slate-50 border border-slate-300 rounded-xl p-3 md:p-4 flex items-center gap-3 flex-wrap">
        <HandCoins className="text-slate-500 shrink-0" size={18} />
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-bold text-slate-900">Günlük Tahsilat Takibi</div>
          <div className="text-xs text-slate-600 mt-0.5">Müvekkil listeden seçilir, Gönderilecek Tutar otomatik hesaplanır (Anapara + Faiz + Masraf).</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={openCreate}
            data-testid="tahsilat-add-btn"
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-2 rounded-md"
          >
            <Plus size={14} /> Yeni Tahsilat
          </button>
          <label className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-2 rounded-md cursor-pointer">
            <Upload size={14} /> Excel Yükle
            <input type="file" accept=".xlsx" onChange={handleImport} className="hidden" data-testid="tahsilat-import-input" />
          </label>
          <button
            onClick={handleImportTemplate}
            data-testid="tahsilat-import-template"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3 py-2 rounded-md"
          >
            <Download size={14} /> Şablon
          </button>
          <button
            onClick={() => setShowExport(true)}
            data-testid="tahsilat-export-btn"
            className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-2 rounded-md"
          >
            <Download size={14} /> Dışarı Aktar
            {selectedIds.size > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white text-emerald-700 text-[10px] font-bold">
                {selectedIds.size}
              </span>
            )}
          </button>
          {selectedIds.size > 0 && (
            <>
              <button
                onClick={() => bulkSetGonderimDurumu("gonderilmeyecek")}
                data-testid="tahsilat-bulk-not-sending-btn"
                className="inline-flex items-center gap-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-semibold px-3 py-2 rounded-md"
                title="Seçili satırları Gönderilmeyecek olarak işaretle"
              >
                <Ban size={14} /> Gönderilmeyecek
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white text-yellow-700 text-[10px] font-bold">
                  {selectedIds.size}
                </span>
              </button>
              <button
                onClick={() => bulkSetGonderimDurumu("gonderilecek")}
                data-testid="tahsilat-bulk-sending-btn"
                className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold px-3 py-2 rounded-md"
                title="Seçili satırları Gönderilecek olarak işaretle"
              >
                <Send size={14} /> Gönderilecek
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white text-emerald-700 text-[10px] font-bold">
                  {selectedIds.size}
                </span>
              </button>
              <BulkDosyaDurumu ids={[...selectedIds]} onDone={() => { load(); setOzetKey((k) => k + 1); }} />
              <button
                onClick={() => setShowBulkEdit(true)}
                data-testid="tahsilat-bulk-edit-btn"
                className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-2 rounded-md"
              >
                <Edit size={14} /> Toplu Düzenle
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white text-amber-700 text-[10px] font-bold">
                  {selectedIds.size}
                </span>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Müvekkil, müşteri, keşideci, ödemeyi yapan, icra föy, dosya no..."
              className="w-full pl-7 pr-3 py-1.5 border border-slate-300 rounded-md text-sm focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none"
              data-testid="tahsilat-search"
            />
          </div>
          <button onClick={load} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-md">Ara</button>
          <button
            onClick={() => setFiltersOpen((x) => !x)}
            data-testid="tahsilat-filter-toggle"
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md ${
              filtersOpen || activeFilterCount > 0 ? "bg-indigo-600 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
            }`}
          >
            <Filter size={13} /> Filtreler
            {activeFilterCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white text-indigo-700 text-[10px] font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-900 font-semibold">Temizle</button>
          )}
          <DosyaDurumuFilter value={colFilters.dosya_durumu?.value || ""}
            onChange={(v) => { setColFilters((c) => ({ ...c, dosya_durumu: v ? { value: v } : undefined })); setFilterTick((t) => t + 1); }} />
          <span className="text-xs text-slate-500 ml-auto">
            {rows.length} kayıt{selectedIds.size > 0 && <> · <strong className="text-emerald-700">{selectedIds.size} seçili</strong></>}
          </span>
        </div>

        {filtersOpen && (
          <div className="mb-3 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3" data-testid="tahsilat-filters-panel">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Tahsilat Tarihi — Başlangıç</label>
                <DateInput value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-sm" data-testid="filter-from-date" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Tahsilat Tarihi — Bitiş</label>
                <DateInput value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-sm" data-testid="filter-to-date" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Gönderme Tarihi — Başlangıç</label>
                <DateInput value={filters.from_gonderme} onChange={(e) => setFilters({ ...filters, from_gonderme: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-sm" data-testid="filter-from-gonderme" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Gönderme Tarihi — Bitiş</label>
                <DateInput value={filters.to_gonderme} onChange={(e) => setFilters({ ...filters, to_gonderme: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-sm" data-testid="filter-to-gonderme" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Müvekkil</label>
                <select value={filters.muvekkil} onChange={(e) => setFilters({ ...filters, muvekkil: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-sm" data-testid="filter-muvekkil">
                  <option value="">Hepsi</option>
                  {creditors.map((c) => <option key={c.id} value={c.ad}>{c.ad}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Personel</label>
                <select value={filters.personnel_id} onChange={(e) => setFilters({ ...filters, personnel_id: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-sm" data-testid="filter-personnel">
                  <option value="">Hepsi</option>
                  {personnel.map((p) => <option key={p.id} value={p.id}>{p.ad_soyad}</option>)}
                </select>
              </div>
            </div>

            {/* Universal per-column filters */}
            <div className="border-t border-slate-200 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Columns3 size={13} className="text-slate-500" />
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Sütun Filtreleri</div>
                <select
                  value={addColKey}
                  onChange={(e) => {
                    const k = e.target.value;
                    if (!k) return;
                    setColFilters((prev) => ({ ...prev, [k]: prev[k] || (COLUMN_META_BY_KEY[k].t === "text" ? { value: "" } : { min: "", max: "" }) }));
                    setAddColKey("");
                  }}
                  className="ml-auto text-xs px-2 py-1 border border-slate-300 rounded bg-white"
                  data-testid="filter-add-column"
                >
                  <option value="">+ Sütun Ekle</option>
                  {COLUMN_META.filter((c) => !colFilters[c.k]).map((c) => (
                    <option key={c.k} value={c.k}>{c.l}</option>
                  ))}
                </select>
              </div>
              {Object.keys(colFilters).length === 0 && (
                <div className="text-[11px] text-slate-400 italic">Henüz sütun filtresi eklenmedi. İstediğiniz sütun için metin arama / sayı veya tarih aralığı tanımlayabilirsiniz.</div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(colFilters).map(([k, cond]) => {
                  const meta = COLUMN_META_BY_KEY[k];
                  if (!meta) return null;
                  const update = (patch) => setColFilters((prev) => ({ ...prev, [k]: { ...prev[k], ...patch } }));
                  const remove = () => setColFilters((prev) => { const { [k]: _, ...rest } = prev; return rest; });
                  return (
                    <div key={k} className="bg-white border border-slate-200 rounded-md p-2 flex items-center gap-2" data-testid={`colfilter-row-${k}`}>
                      <div className="text-[11px] font-bold text-slate-700 min-w-[120px] truncate" title={meta.l}>{meta.l}</div>
                      {meta.t === "text" && (
                        <input
                          type="text"
                          value={cond.value || ""}
                          onChange={(e) => update({ value: e.target.value })}
                          placeholder="içeren..."
                          className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs"
                          data-testid={`colfilter-value-${k}`}
                        />
                      )}
                      {meta.t === "number" && (
                        <div className="flex-1 flex items-center gap-1">
                          <input type="number" step="0.01" value={cond.min || ""} onChange={(e) => update({ min: e.target.value })} placeholder="min" className="w-full px-2 py-1 border border-slate-300 rounded text-xs" data-testid={`colfilter-min-${k}`} />
                          <span className="text-slate-400 text-xs">—</span>
                          <input type="number" step="0.01" value={cond.max || ""} onChange={(e) => update({ max: e.target.value })} placeholder="max" className="w-full px-2 py-1 border border-slate-300 rounded text-xs" data-testid={`colfilter-max-${k}`} />
                        </div>
                      )}
                      {meta.t === "date" && (
                        <div className="flex-1 flex items-center gap-1">
                          <DateInput value={cond.min || ""} onChange={(e) => update({ min: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" data-testid={`colfilter-min-${k}`} />
                          <span className="text-slate-400 text-xs">—</span>
                          <DateInput value={cond.max || ""} onChange={(e) => update({ max: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" data-testid={`colfilter-max-${k}`} />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={remove}
                        className="shrink-0 w-6 h-6 rounded hover:bg-rose-100 text-rose-600 grid place-items-center"
                        data-testid={`colfilter-remove-${k}`}
                        title="Kaldır"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-slate-200">
              <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none" data-testid="filter-hide-not-sending-label">
                <input
                  type="checkbox"
                  checked={hideNotSending}
                  onChange={(e) => { setHideNotSending(e.target.checked); setTimeout(load, 50); }}
                  data-testid="filter-hide-not-sending"
                />
                <Ban size={13} className="text-yellow-600" />
                <span className="font-semibold">Gönderilmeyecekleri gizle</span>
              </label>
              <QuickDateFilters
                label="Tahsilat Tarihi"
                testPrefix="quick-tahsilat"
                onApply={({ from, to }) => { setFilters((f) => ({ ...f, from_date: from, to_date: to })); setTimeout(load, 50); }}
              />
              <QuickDateFilters
                label="Gönderme Tarihi"
                testPrefix="quick-gonderme"
                onApply={({ from, to }) => { setFilters((f) => ({ ...f, from_gonderme: from, to_gonderme: to })); setTimeout(load, 50); }}
              />
            </div>
            <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-slate-200">
              <SavedFilterPresets
                scope="tahsilat"
                currentFilters={filters}
                currentColFilters={colFilters}
                onApply={(f, cf) => { setFilters({ from_date: "", to_date: "", muvekkil: "", personnel_id: "", from_gonderme: "", to_gonderme: "", ...f }); setColFilters(cf || {}); setTimeout(load, 50); }}
              />
              <button onClick={load} data-testid="filter-apply-btn" className="ml-auto px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-md">Uygula</button>
            </div>
          </div>
        )}

        <div className="sticky-table">
          <table className="tahsilat-table w-full text-xs" data-testid="tahsilat-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th style={{ width: 32 }} className="px-2 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    data-testid="tahsilat-select-all"
                    title="Tümünü seç"
                  />
                </th>
                <th style={{ width: 24 }}></th>
                <SortTh k="tahsilat_tarihi" label="Tahsilat Tarihi" sort={colSort.sort} onSort={colSort.toggle} className="text-left px-2 py-2 font-bold text-slate-700" testid="tahsilat-sort-tahsilat_tarihi" />
                <SortTh k="muvekkil" label="Müvekkil" sort={colSort.sort} onSort={colSort.toggle} className="text-left px-2 py-2 font-bold text-slate-700" testid="tahsilat-sort-muvekkil" />
                <SortTh k="musteri" label="Müşteri" sort={colSort.sort} onSort={colSort.toggle} className="text-left px-2 py-2 font-bold text-slate-700" testid="tahsilat-sort-musteri" />
                <SortTh k="kesideci" label="Keşideci" sort={colSort.sort} onSort={colSort.toggle} className="text-left px-2 py-2 font-bold text-slate-700" testid="tahsilat-sort-kesideci" />
                <SortTh k="odemeyi_yapan_taraf" label="Ödemeyi Yapan" sort={colSort.sort} onSort={colSort.toggle} className="text-left px-2 py-2 font-bold text-slate-700" testid="tahsilat-sort-odemeyi_yapan_taraf" />
                <SortTh k="icra_foy" label="İcra Föy" sort={colSort.sort} onSort={colSort.toggle} className="text-left px-2 py-2 font-bold text-slate-700" testid="tahsilat-sort-icra_foy" />
                <SortTh k="dosya_no" label="Dosya No" sort={colSort.sort} onSort={colSort.toggle} className="text-left px-2 py-2 font-bold text-slate-700" testid="tahsilat-sort-dosya_no" />
                <SortTh k="tahsilat_miktari" label="Tahsilat" sort={colSort.sort} onSort={colSort.toggle} className="text-right px-2 py-2 font-bold text-slate-700" testid="tahsilat-sort-tahsilat_miktari" />
                <SortTh k="vekalet_ucreti" label="Vekalet" sort={colSort.sort} onSort={colSort.toggle} className="text-right px-2 py-2 font-bold text-slate-700" testid="tahsilat-sort-vekalet_ucreti" />
                <th className="text-left px-2 py-2 font-bold text-slate-700">Araştıran</th>
                <th className="text-left px-2 py-2 font-bold text-slate-700">Gidenler</th>
                <SortTh k="gonderim_durumu" label="Durum" sort={colSort.sort} onSort={colSort.toggle} className="text-center px-2 py-2 font-bold text-slate-700" testid="tahsilat-sort-gonderim_durumu" />
                <th className="text-right px-2 py-2 font-bold text-slate-700"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <LoadingRow colSpan={15} text="Tahsilatlar yükleniyor..." />}
              {!loading && loadError && <tr><td colSpan={15}><LoadError text="Tahsilatlar yüklenemedi." onRetry={load} /></td></tr>}
              {!loading && !loadError && rows.length === 0 && <tr><td colSpan={15} className="text-center text-slate-400 py-6">Kayıt yok</td></tr>}
              {colSort.sorted.map((r) => {
                const aNames = (r.arastiran_personnel_ids || []).map((pid) => persById[pid]?.ad_soyad).filter(Boolean);
                const gNames = (r.gidenler_personnel_ids || []).map((pid) => persById[pid]?.ad_soyad).filter(Boolean);
                const isOpen = expandedId === r.id;
                const isSel = selectedIds.has(r.id);
                const statusCls = rowStatusClass(r);
                const notSending = (r.gonderim_durumu || "gonderilecek").toLowerCase() === "gonderilmeyecek";
                const isHighlight = highlightId === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <tr className={`border-b border-slate-100 cursor-pointer ${statusCls || "hover:bg-slate-50"} ${!statusCls && isOpen ? "bg-slate-50" : ""} ${!statusCls && isSel ? "bg-emerald-50" : ""} ${isHighlight ? "ring-2 ring-sky-500 ring-offset-1 animate-pulse" : ""}`} onClick={() => setExpandedId(isOpen ? null : r.id)} data-testid={`tahsilat-row-${r.id}`}>
                      <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleRowSelect(r.id)}
                          data-testid={`tahsilat-select-${r.id}`}
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        {isOpen ? <ChevronDown size={14} className="text-slate-600" /> : <ChevronRight size={14} className="text-slate-400" />}
                      </td>
                      <td className="px-2 py-2 font-semibold text-slate-900">
                        {fmtDate(r.tahsilat_tarihi) || "-"}
                        <DosyaDurumuBadge v={r.dosya_durumu} id={r.id} tarih={r.dosya_durumu_tarihi} />
                      </td>
                      <td className="px-2 py-2 text-slate-900">{r.muvekkil || "-"}</td>
                      <td className="px-2 py-2 text-slate-700 max-w-[160px]"><span className="block truncate" title={r.musteri || ""} data-testid={`tahsilat-musteri-${r.id}`}>{r.musteri || "-"}</span></td>
                      <td className="px-2 py-2 text-slate-700 max-w-[160px]"><span className="block truncate" title={r.kesideci || ""} data-testid={`tahsilat-kesideci-${r.id}`}>{r.kesideci || "-"}</span></td>
                      <td className="px-2 py-2 text-slate-700 max-w-[160px]"><span className="block truncate" title={r.odemeyi_yapan_taraf || ""} data-testid={`tahsilat-odemeyi-yapan-${r.id}`}>{r.odemeyi_yapan_taraf || "-"}</span></td>
                      <td className="px-2 py-2 text-slate-700">{r.icra_foy || "-"}</td>
                      <td className="px-2 py-2 text-slate-700">{r.dosya_no || "-"}</td>
                      <td className="px-2 py-2 text-right font-mono-tab text-slate-900">{fmtTL(r.tahsilat_miktari)}</td>
                      <td className="px-2 py-2 text-right font-mono-tab text-emerald-700 font-semibold">{fmtTL(r.vekalet_ucreti)}</td>
                      <td className="px-2 py-2">
                        <div className="flex flex-wrap gap-0.5">
                          {aNames.length > 0 ? aNames.map((n, i) => (
                            <span key={i} className="inline-flex items-center text-[10px] font-semibold bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded">{n}</span>
                          )) : <span className="text-slate-400">-</span>}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex flex-wrap gap-0.5">
                          {gNames.length > 0 ? gNames.map((n, i) => (
                            <span key={i} className="inline-flex items-center text-[10px] font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{n}</span>
                          )) : <span className="text-slate-400">-</span>}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => toggleGonderimDurumu(r)}
                          data-testid={`tahsilat-gonderim-toggle-${r.id}`}
                          title={notSending ? "Gönderilmeyecek — tıklayarak Gönderilecek olarak işaretle" : "Gönderilecek — tıklayarak Gönderilmeyecek olarak işaretle"}
                          className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full border transition-colors ${
                            notSending
                              ? "bg-yellow-200 border-yellow-400 text-yellow-900 hover:bg-yellow-300"
                              : "bg-emerald-100 border-emerald-300 text-emerald-800 hover:bg-emerald-200"
                          }`}
                        >
                          {notSending ? <Ban size={11} /> : <Send size={11} />}
                          {notSending ? "Gönderilmeyecek" : "Gönderilecek"}
                        </button>
                      </td>
                      <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => openEdit(r)}
                            className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded"
                            data-testid={`tahsilat-edit-${r.id}`}
                            title="Düzenle"
                          >
                            <Edit size={13} />
                          </button>
                          <button
                            onClick={() => openCopy(r)}
                            className="p-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded"
                            data-testid={`tahsilat-copy-${r.id}`}
                            title="Kopyalayarak yeni tahsilat"
                          >
                            <Copy size={13} />
                          </button>
                          <button
                            onClick={() => handleDelete(r.id)}
                            className="p-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded"
                            data-testid={`tahsilat-delete-${r.id}`}
                            title="Sil"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50">
                        <td></td>
                        <td colSpan={14} className="py-3 px-3">
                          <DetailPanel tahsilatId={r.id} personnel={personnel} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <TahsilatModal
          editing={editing}
          copyFrom={copyFrom}
          personnel={personnel}
          creditors={creditors}
          onCreditorAdded={(c) => setCreditors((prev) => prev.find((x) => x.id === c.id) ? prev : [...prev, c].sort((a, b) => (a.ad || "").localeCompare(b.ad || "", "tr")))}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}

      {importPreview && (
        <ImportPreviewModal
          preview={importPreview}
          onCancel={() => setImportPreview(null)}
          onConfirm={async () => { const f = importPreview.file; setImportPreview(null); await runImport(f); }}
        />
      )}

      {showExport && (
        <ExportModal
          selectedIds={Array.from(selectedIds)}
          totalCount={rows.length}
          creditors={creditors}
          currentFilters={filters}
          currentColFilters={colFilters}
          onClose={() => setShowExport(false)}
        />
      )}

      {showBulkEdit && (
        <BulkEditModal
          selectedIds={Array.from(selectedIds)}
          creditors={creditors}
          onClose={() => setShowBulkEdit(false)}
          onSaved={() => { setShowBulkEdit(false); load(); }}
        />
      )}
    </div>
  );
}


function DashCard({ title, count, tahsilat, vekalet, fmt, tone, showT = true, showV = true, onClick, testid }) {
  const toneMap = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
    sky: "bg-sky-50 border-sky-200 text-sky-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
  };
  return (
    <div className={`border rounded-lg p-3 ${toneMap[tone] || toneMap.slate} ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
      onClick={onClick} data-testid={testid} role={onClick ? "button" : undefined} title={onClick ? "Listeyi aç" : undefined}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{title}{!showT && " · Vekalet"}</div>
      <div className="text-xl font-bold mt-0.5">{fmt(showT ? tahsilat : vekalet)} ₺</div>
      <div className="text-[11px] opacity-80 mt-0.5">
        {showT && showV && <>Vekalet: <strong>{fmt(vekalet)} ₺</strong> · </>}{count} kayıt
      </div>
    </div>
  );
}


function DetailPanel({ tahsilatId, personnel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/tahsilat/${tahsilatId}/detail`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Detay yüklenemedi"))
      .finally(() => setLoading(false));
  }, [tahsilatId]);

  const fmt = (v) => (v == null || v === "") ? "-" : Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) return <div className="text-xs text-slate-400">Yükleniyor...</div>;
  if (!data) return <div className="text-xs text-slate-400">Veri yok</div>;

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid={`detail-panel-${tahsilatId}`}>
      {/* Linked prims */}
      <div className="bg-white border border-slate-200 rounded-md p-3">
        <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-2">
          Bağlı Primler ({data.linked_prims.length})
        </div>
        {data.linked_prims.length === 0 && (
          <div className="text-xs text-slate-400">Bu tahsilata bağlı prim yok</div>
        )}
        <div className="space-y-1.5">
          {data.linked_prims.map((p) => (
            <div key={p.id} className="bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
              <div className="text-xs font-semibold text-slate-900">
                {p.alacakli_adi || "-"} · {p.icra_foy}/{p.icra_esas}
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5 flex items-center gap-2 flex-wrap">
                <span>Vekalet: <strong className="text-emerald-700">{fmt(p.vekalet_ucreti)} ₺</strong></span>
                {p.personnel_names?.filter(Boolean).length > 0 && (
                  <>
                    <span>·</span>
                    <span>Gidenler:</span>
                    {p.personnel_names.filter(Boolean).map((n, i) => (
                      <span key={i} className="inline-flex text-[10px] font-semibold bg-amber-100 text-amber-800 px-1 py-0.5 rounded">{n}</span>
                    ))}
                  </>
                )}
                {p.arastiran_name && (
                  <>
                    <span>·</span>
                    <span>Araştıran:</span>
                    <span className="inline-flex text-[10px] font-semibold bg-indigo-100 text-indigo-800 px-1 py-0.5 rounded">{p.arastiran_name}</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Missing gidenler */}
      <div className="bg-white border border-slate-200 rounded-md p-3">
        <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-2 flex items-center gap-1">
          <AlertCircle size={12} /> Eksik Prim Girişi ({data.missing_gidenler.length})
        </div>
        {data.missing_gidenler.length === 0 && (
          <div className="text-xs text-slate-400">Tüm gidenler için prim mevcut</div>
        )}
        <div className="space-y-1">
          {data.missing_gidenler.map((m) => (
            <div key={m.id} className="bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-xs">
              <strong className="text-amber-900">{m.ad_soyad}</strong>
              {m.sicil_no && <span className="text-amber-700"> · {m.sicil_no}</span>}
            </div>
          ))}
        </div>
        {data.pending_notifications.length > 0 && (
          <div className="mt-2 text-[11px] text-slate-500">
            {data.pending_notifications.length} admin bildirimi bekliyor
          </div>
        )}
      </div>
    </div>
  );
}


function MuvekkilDropdown({ value, onChange, creditors, onCreditorAdded }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    const ad = (newName || "").trim();
    if (!ad) { toast.error("Müvekkil adı boş olamaz"); return; }
    setSaving(true);
    try {
      const r = await api.post("/creditors", { ad });
      const c = r.data;
      onCreditorAdded(c);
      onChange(c.ad);
      setAdding(false);
      setNewName("");
      toast.success(`Müvekkil eklendi: ${c.ad}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        required
        data-testid="tahsilat-muvekkil-select"
        className="flex-1 px-2 py-1.5 border border-indigo-300 rounded-md text-sm bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-200 outline-none"
      >
        <option value="">— Müvekkil seçin —</option>
        {creditors.map((c) => (
          <option key={c.id} value={c.ad}>{c.ad}</option>
        ))}
      </select>
      {!adding && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-md bg-indigo-50 hover:bg-indigo-100 text-indigo-700"
          title="Yeni müvekkil ekle"
          data-testid="tahsilat-muvekkil-new"
        >
          <Plus size={14} />
        </button>
      )}
      {adding && (
        <div className="flex items-center gap-1">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAdd())}
            autoFocus
            placeholder="Yeni müvekkil adı"
            className="px-2 py-1.5 border border-slate-300 rounded-md text-sm"
            data-testid="tahsilat-muvekkil-new-input"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={saving}
            className="px-2 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md disabled:opacity-50"
          >
            {saving ? "..." : "Ekle"}
          </button>
          <button
            type="button"
            onClick={() => { setAdding(false); setNewName(""); }}
            className="px-2 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs rounded-md"
          >
            İptal
          </button>
        </div>
      )}
    </div>
  );
}




const emptyTahsilatForm = () => {
  const base = { muvekkil: "", toplam_gonderilecek: "" };
  FIELD_GROUPS.forEach((g) => g.fields.forEach((f) => (base[f.k] = "")));
  base.arastiran_personnel_ids = [];
  base.gidenler_personnel_ids = [];
  base.arastiran_text = "";
  base.gidenler_text = "";
  return base;
};

const draftHasContent = (f) => Object.entries(f).some(([k, v]) =>
  k !== "toplam_gonderilecek" && (Array.isArray(v) ? v.length > 0 : String(v ?? "").trim() !== ""));

function readDraft(key) {
  try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; }
}

const COPY_KEYS = ["muvekkil", "musteri_no", "islem_no", "cek_mikro_no", "vade", "icra_foy", "icra_mudurlugu", "dosya_no", "musteri", "kesideci",
  "odemeyi_yapan_taraf", "arastiran_personnel_ids", "gidenler_personnel_ids"];

const _normAd = (s) => (s || "").trim().replace(/\s+/g, " ").toLocaleLowerCase("tr-TR");
const guessOdeyen = (f) => {
  const o = _normAd(f.odemeyi_yapan_taraf);
  return !o ? "" : o === _normAd(f.musteri) ? "Müşteri" : o === _normAd(f.kesideci) ? "Keşideci" : "";
};
const withOdeyen = (f) => (f.odeyen_turu ? f : { ...f, odeyen_turu: guessOdeyen(f), _autoOdeyen: !!guessOdeyen(f) });

function TahsilatModal({ editing, copyFrom, personnel, creditors, onCreditorAdded, onClose, onSaved }) {
  const { user } = useAuth();
  const draftKey = !editing && user ? `tahsilat_draft_${user.id || user.email}` : null;
  const [draftInfo, setDraftInfo] = useState(() => (draftKey && !copyFrom ? readDraft(draftKey)?.saved_at || null : null));
  const [form, setForm] = useState(() => {
    if (editing) return withOdeyen({ ...emptyTahsilatForm(), ...editing });
    if (copyFrom) {
      const f = emptyTahsilatForm();
      COPY_KEYS.forEach((k) => { if (copyFrom[k] != null) f[k] = Array.isArray(copyFrom[k]) ? [...copyFrom[k]] : copyFrom[k]; });
      return withOdeyen(f);
    }
    const d = draftKey ? readDraft(draftKey) : null;
    return { ...emptyTahsilatForm(), ...(d?.form || {}) };
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!draftKey) return;
    if (draftHasContent(form)) localStorage.setItem(draftKey, JSON.stringify({ form, saved_at: new Date().toISOString() }));
    else localStorage.removeItem(draftKey);
  }, [form, draftKey]);

  const discardDraft = () => {
    if (draftKey) localStorage.removeItem(draftKey);
    setForm(emptyTahsilatForm());
    setDraftInfo(null);
    toast.success("Taslak silindi");
  };

  const setField = (k, v) => setForm((f) => {
    const n = { ...f, [k]: v };
    if (["odemeyi_yapan_taraf", "musteri", "kesideci"].includes(k) && (!f.odeyen_turu || f._autoOdeyen)) {
      n.odeyen_turu = guessOdeyen(n);
      n._autoOdeyen = !!n.odeyen_turu;
    }
    if (k === "odeyen_turu") n._autoOdeyen = false;
    return n;
  });
  const togglePerson = (listKey, pid) => setForm((f) => {
    const list = f[listKey] || [];
    return { ...f, [listKey]: list.includes(pid) ? list.filter((x) => x !== pid) : [...list, pid] };
  });

  // Auto-calc Toplam Gönderilecek = Anapara + Faiz + Masraf
  const autoToplam = useMemo(() => {
    const ap = parseFloat(form.ana_para) || 0;
    const fz = parseFloat(form.faiz) || 0;
    const msr = parseFloat(form.masraf_avans) || 0;
    const sum = ap + fz + msr;
    return sum > 0 ? sum.toFixed(2) : "";
  }, [form.ana_para, form.faiz, form.masraf_avans]);

  useEffect(() => {
    setForm((f) => ({ ...f, toplam_gonderilecek: autoToplam }));
  }, [autoToplam]);

  const submit = async (e) => {
    e.preventDefault();
    if (!(form.muvekkil || "").trim()) {
      toast.error("Müvekkil seçimi zorunludur");
      return;
    }
    setSaving(true);
    try {
      const allowed = new Set([...COLUMN_META.map((c) => c.k), "arastiran_personnel_ids", "gidenler_personnel_ids", "gonderim_durumu"]);
      const payload = Object.fromEntries(Object.entries(form).filter(([k]) => allowed.has(k)));
      // Convert empty money fields to null; coerce numeric strings
      const moneyKeys = new Set(["toplam_gonderilecek"]);
      FIELD_GROUPS.forEach((g) => g.fields.forEach((f) => f.type === "money" && moneyKeys.add(f.k)));
      moneyKeys.forEach((k) => {
        if (payload[k] === "" || payload[k] == null) payload[k] = null;
        else if (typeof payload[k] === "string") payload[k] = Number(payload[k]) || null;
      });
      if (editing) {
        await api.put(`/tahsilat/${editing.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post("/tahsilat", payload);
        if (draftKey) localStorage.removeItem(draftKey);
        toast.success("Eklendi");
      }
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map((x) => `${COLUMN_META_BY_KEY[x?.loc?.[x.loc.length - 1]]?.l || x?.loc?.slice(-1)[0] || ""}: ${x?.type === "string_too_long" ? "en fazla 1000 karakter olabilir" : x?.msg}`).join(" · ")
        : (typeof d === "string" ? d : "Kaydedilemedi");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onMouseDown={(e) => { e.currentTarget.dataset.down = e.target === e.currentTarget ? "1" : ""; }}
      onClick={(e) => { if (e.target === e.currentTarget && e.currentTarget.dataset.down === "1") onClose(); }}
      data-testid="tahsilat-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-slate-900">{editing ? "Tahsilat Düzenle" : copyFrom ? "Yeni Tahsilat (Kopya)" : "Yeni Tahsilat"}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-5">
          {copyFrom && (
            <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2 text-xs text-indigo-900" data-testid="tahsilat-copy-banner">
              <Copy size={14} className="text-indigo-600 shrink-0" />
              <span className="flex-1">
                <strong>{copyFrom.icra_foy || copyFrom.dosya_no || "Seçili kayıt"}</strong> kaydından kopyalandı: müvekkil, dosya, taraf, işlem no, çek/mikro no ve vade dolduruldu; tutar ve tahsilat tarihi boş.
              </span>
            </div>
          )}
          {draftInfo && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-900" data-testid="tahsilat-draft-banner">
              <AlertCircle size={14} className="text-amber-600 shrink-0" />
              <span className="flex-1">
                Yarım kalan taslak geri yüklendi · {new Date(draftInfo).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}
              </span>
              <button type="button" onClick={discardDraft} data-testid="tahsilat-draft-discard"
                className="font-semibold text-amber-900 hover:text-rose-700 underline underline-offset-2">Taslağı Sil</button>
            </div>
          )}
          {!editing && !draftInfo && !copyFrom && draftHasContent(form) && (
            <div className="text-[11px] text-slate-500 -mt-2" data-testid="tahsilat-draft-autosave">Taslak otomatik saklanıyor · pencere kapansa da kaybolmaz</div>
          )}
          {/* Müvekkil (required dropdown) at the top */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3">
            <label className="text-xs font-bold uppercase tracking-wider text-indigo-800 block mb-1.5">
              Müvekkil <span className="text-rose-600">*</span>
            </label>
            <MuvekkilDropdown
              value={form.muvekkil}
              onChange={(v) => setField("muvekkil", v)}
              creditors={creditors}
              onCreditorAdded={onCreditorAdded}
            />
            <div className="text-[10px] text-indigo-600 mt-1.5">
              Yalnızca listeden seçilebilir. Yeni müvekkil için <strong>+</strong> butonunu kullanın.
            </div>
          </div>

          {FIELD_GROUPS.map((g) => (
            <div key={g.title}>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">{g.title}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {g.fields.map((f) => (
                  <div key={f.k}>
                    <label className={`text-xs font-semibold block mb-1 ${f.important ? "text-indigo-700" : "text-slate-700"}`}>{f.l}{f.important && " ★"}</label>
                    {TEMPLATE_FIELDS.has(f.k) ? (
                      <TemplateInput kind={f.k} label={f.l} value={form[f.k]} onChange={(v) => setField(f.k, v)} testid={`tahsilat-field-${f.k}`} />
                    ) : f.type === "select" ? (
                      <select value={form[f.k] ?? ""} onChange={(e) => setField(f.k, e.target.value)} data-testid={`tahsilat-field-${f.k}`}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white focus:border-slate-900 outline-none">
                        {f.options.map((o) => <option key={o} value={o}>{o || "— Seçiniz —"}</option>)}
                      </select>
                    ) : f.type === "date" ? (
                      <DateInput value={form[f.k] ?? ""} onChange={(e) => setField(f.k, e.target.value)} data-testid={`tahsilat-field-${f.k}`}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none" />
                    ) : (
                    <input
                      type={f.type === "money" ? "number" : "text"}
                      step={f.type === "money" ? "0.01" : undefined}
                      maxLength={f.type === "money" || f.type === "date" ? undefined : 1000}
                      value={form[f.k] ?? ""}
                      onChange={(e) => setField(f.k, e.target.value)}
                      data-testid={`tahsilat-field-${f.k}`}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none"
                    />
                    )}
                  </div>
                ))}
                {/* Auto-calc "Toplam Gönderilecek" in the Tutarlar group */}
                {g.title === "Tutarlar" && (
                  <div>
                    <label className="text-xs font-semibold block mb-1 text-emerald-700">
                      Toplam Gönderilecek (₺) <span className="text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded ml-1">OTOMATİK</span>
                    </label>
                    <input
                      type="text"
                      value={form.toplam_gonderilecek !== "" && form.toplam_gonderilecek != null
                        ? Number(form.toplam_gonderilecek).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺"
                        : ""}
                      readOnly
                      tabIndex={-1}
                      data-testid="tahsilat-field-toplam_gonderilecek"
                      className="w-full px-2 py-1.5 border border-emerald-300 rounded-md text-sm font-semibold text-emerald-800 bg-emerald-50 cursor-not-allowed"
                      placeholder="Anapara + Faiz + Masraf"
                    />
                    <div className="text-[10px] text-emerald-600 mt-0.5">= Anapara + Faiz + Masraf</div>
                  </div>
                )}
              </div>
            </div>
          ))}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PersonnelMultiselect
              label="ARAŞTIRAN"
              personnel={personnel}
              selectedIds={form.arastiran_personnel_ids || []}
              onToggle={(pid) => togglePerson("arastiran_personnel_ids", pid)}
              testid="arastiran"
              filterFn={(p) => isArastirmaPozisyon(p.pozisyon)}
              emptyHint="Araştırma pozisyonunda aktif personel yok"
            />
            <PersonnelMultiselect
              label="HACZE GİDENLER"
              personnel={personnel}
              selectedIds={form.gidenler_personnel_ids || []}
              onToggle={(pid) => togglePerson("gidenler_personnel_ids", pid)}
              testid="gidenler"
              filterFn={(p) => isHacizBirimiPozisyon(p.pozisyon) || isCallCenterPozisyon(p.pozisyon)}
              emptyHint="Haciz Birimi veya Call Center pozisyonunda aktif personel yok"
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-900 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <div>
              GİDENLER'de seçilen personel için (İCRA FÖY + DOSYA NO) ile eşleşen prim yoksa, admin'e otomatik uyarı düşer.
              Personel prim eklediğinde otomatik eşlenir.
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50">İptal</button>
            <button type="submit" disabled={saving} data-testid="tahsilat-save-btn" className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-lg text-sm font-semibold">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function PersonnelMultiselect({ label, personnel, selectedIds, onToggle, testid, filterFn, emptyHint }) {
  const [filter, setFilter] = useState("");
  const scoped = filterFn ? personnel.filter(filterFn) : personnel;
  const filtered = scoped.filter((p) =>
    p.ad_soyad.toLowerCase().includes(filter.toLowerCase()) ||
    (p.sicil_no || "").toLowerCase().includes(filter.toLowerCase())
  );
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1">{label} <span className="text-slate-400 font-normal">({selectedIds.length} seçili · {scoped.length} uygun)</span></label>
      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Personel ara..."
        className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm mb-2 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none"
      />
      <div className="border border-slate-200 rounded-md max-h-44 overflow-y-auto p-1 space-y-0.5" data-testid={`${testid}-multiselect`}>
        {filtered.length === 0 && (
          <div className="text-[11px] italic text-slate-400 px-2 py-1.5">
            {emptyHint || "Uygun personel bulunamadı"}
          </div>
        )}
        {filtered.map((p) => (
          <label key={p.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer hover:bg-slate-50 px-1.5 py-1 rounded">
            <input
              type="checkbox"
              checked={selectedIds.includes(p.id)}
              onChange={() => onToggle(p.id)}
              data-testid={`${testid}-${p.id}`}
            />
            <span className="font-semibold flex-1">{p.ad_soyad}</span>
            <span className="text-[10px] text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded">{p.pozisyon || "-"}</span>
            <span className="text-slate-400">{p.sicil_no}</span>
          </label>
        ))}
      </div>
    </div>
  );
}


function ExportModal({ selectedIds, totalCount, creditors, currentFilters, currentColFilters, onClose }) {
  const hasSelection = selectedIds.length > 0;
  const [scope, setScope] = useState(hasSelection ? "selected" : "all");
  const [format, setFormat] = useState("xlsx");
  const [muvekkil, setMuvekkil] = useState(currentFilters.muvekkil || "");
  const [fromDate, setFromDate] = useState(currentFilters.from_date || "");
  const [toDate, setToDate] = useState(currentFilters.to_date || "");
  const [fromGonderme, setFromGonderme] = useState(currentFilters.from_gonderme || "");
  const [toGonderme, setToGonderme] = useState(currentFilters.to_gonderme || "");
  const [grouped, setGrouped] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // XLSX column selection — all selected by default.
  const [selectedCols, setSelectedCols] = useState(() => new Set(COLUMN_META.map((c) => c.k)));
  const [colsOpen, setColsOpen] = useState(false);
  const toggleCol = (k) => setSelectedCols((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const selectAllCols = () => setSelectedCols(new Set(COLUMN_META.map((c) => c.k)));
  const clearCols = () => setSelectedCols(new Set());
  const activeColFilterCount = countColFilters(currentColFilters);

  const handleDownload = async () => {
    const params = new URLSearchParams();
    if (scope === "selected") {
      if (selectedIds.length === 0) { toast.error("Seçili satır yok"); return; }
      params.set("ids", selectedIds.join(","));
    } else if (scope === "filtered") {
      if (muvekkil) params.set("muvekkil", muvekkil);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      if (fromGonderme) params.set("from_gonderme", fromGonderme);
      if (toGonderme) params.set("to_gonderme", toGonderme);
      // Carry the universal column filters currently applied on the main list.
      const cf = serializeColFilters(currentColFilters);
      if (cf) params.set("cf", cf);
    }
    params.set("format", format);
    if (format === "pdf" && grouped) params.set("grouped", "true");
    // Column selection → XLSX/CSV only. Omit when all are chosen (keeps default).
    const supportsColumnSelect = format === "xlsx" || format === "csv";
    if (supportsColumnSelect && selectedCols.size > 0 && selectedCols.size < COLUMN_META.length) {
      const ordered = COLUMN_META.filter((c) => selectedCols.has(c.k)).map((c) => c.k);
      params.set("columns", ordered.join(","));
    }
    if (supportsColumnSelect && selectedCols.size === 0) {
      toast.error("En az bir sütun seçmelisiniz");
      return;
    }

    setDownloading(true);
    try {
      const r = await api.get(`/tahsilat/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `tahsilatlar_${ts}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} indirildi`);
      onClose();
    } catch {
      toast.error("Dışarı aktarım başarısız");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onMouseDown={(e) => { e.currentTarget.dataset.down = e.target === e.currentTarget ? "1" : ""; }}
      onClick={(e) => { if (e.target === e.currentTarget && e.currentTarget.dataset.down === "1") onClose(); }}
      data-testid="tahsilat-export-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Download size={16} className="text-emerald-600" />
            <h3 className="text-sm font-bold text-slate-900">Tahsilatları Dışarı Aktar</h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={14} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Scope */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-2">Kapsam</div>
            <div className="grid grid-cols-1 gap-2">
              <ScopeOption
                checked={scope === "selected"}
                disabled={!hasSelection}
                onClick={() => hasSelection && setScope("selected")}
                title={`Seçili satırlar (${selectedIds.length})`}
                desc={hasSelection ? `${selectedIds.length} satır dışarı aktarılacak` : "Önce satır seçin"}
                testid="export-scope-selected"
              />
              <ScopeOption
                checked={scope === "all"}
                onClick={() => setScope("all")}
                title={`Tüm satırlar (${totalCount})`}
                desc="Hiçbir filtre uygulanmadan tüm kayıtlar aktarılır"
                testid="export-scope-all"
              />
              <ScopeOption
                checked={scope === "filtered"}
                onClick={() => setScope("filtered")}
                title="Müvekkil / Tarih filtresi"
                desc="Aşağıdaki filtrelere uyan kayıtlar aktarılır"
                testid="export-scope-filtered"
              />
            </div>
          </div>

          {/* Filtered scope options */}
          {scope === "filtered" && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2.5" data-testid="export-filter-panel">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Müvekkil</label>
                <select
                  value={muvekkil}
                  onChange={(e) => setMuvekkil(e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
                  data-testid="export-muvekkil"
                >
                  <option value="">Hepsi</option>
                  {creditors.map((c) => <option key={c.id} value={c.ad}>{c.ad}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Tahsilat Tarihi — Başlangıç</label>
                  <DateInput value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="export-from-date" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Tahsilat Tarihi — Bitiş</label>
                  <DateInput value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="export-to-date" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Gönderme Tarihi — Başlangıç</label>
                  <DateInput value={fromGonderme} onChange={(e) => setFromGonderme(e.target.value)} className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="export-from-gonderme" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Gönderme Tarihi — Bitiş</label>
                  <DateInput value={toGonderme} onChange={(e) => setToGonderme(e.target.value)} className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="export-to-gonderme" />
                </div>
              </div>
            </div>
          )}

          {/* Format */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-2">Format</div>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setFormat("xlsx")}
                data-testid="export-format-xlsx"
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-md border text-sm font-semibold transition-colors ${
                  format === "xlsx" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              >
                <FileSpreadsheet size={16} /> Excel
              </button>
              <button
                type="button"
                onClick={() => setFormat("csv")}
                data-testid="export-format-csv"
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-md border text-sm font-semibold transition-colors ${
                  format === "csv" ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
                title="UTF-8 BOM + ; ayracı (Excel-TR ve muhasebe programları için)"
              >
                <FileSpreadsheet size={16} /> CSV
              </button>
              <button
                type="button"
                onClick={() => setFormat("pdf")}
                data-testid="export-format-pdf"
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-md border text-sm font-semibold transition-colors ${
                  format === "pdf" ? "bg-rose-600 text-white border-rose-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              >
                <FileText size={16} /> PDF
              </button>
            </div>
            {format === "csv" && (
              <div className="mt-2 text-[11px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-2 py-1.5">
                CSV, Excel-TR ile uyumlu (<code>;</code> ayracı, UTF-8 BOM, virgüllü ondalık). Muhasebe programları için ideal.
              </div>
            )}
          </div>

          {/* Grouped PDF toggle (only for PDF format) */}
          {format === "pdf" && (
            <label className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-md p-2.5 cursor-pointer" data-testid="export-grouped-toggle-wrap">
              <input
                type="checkbox"
                checked={grouped}
                onChange={(e) => setGrouped(e.target.checked)}
                data-testid="export-grouped-toggle"
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-rose-900">Müvekkil bazlı grup PDF</div>
                <div className="text-[11px] text-rose-700 mt-0.5">Her müvekkil için ayrı sayfa + grup toplamı. Kapak sayfasında müvekkil bazlı genel özet listelenir.</div>
              </div>
            </label>
          )}

          {/* XLSX / CSV column selection */}
          {(format === "xlsx" || format === "csv") && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="export-columns-panel">
              <button
                type="button"
                onClick={() => setColsOpen((x) => !x)}
                className="w-full flex items-center justify-between text-left"
                data-testid="export-columns-toggle"
              >
                <div className="flex items-center gap-2">
                  <Columns3 size={14} className="text-indigo-600" />
                  <span className="text-sm font-semibold text-slate-800">Dışarı Aktarılacak Sütunlar</span>
                  <span className="text-[11px] font-bold bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded">
                    {selectedCols.size}/{COLUMN_META.length}
                  </span>
                </div>
                {colsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {colsOpen && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={selectAllCols} className="text-[11px] font-semibold text-indigo-700 hover:underline" data-testid="export-cols-all">Tümünü Seç</button>
                    <span className="text-slate-300">·</span>
                    <button type="button" onClick={clearCols} className="text-[11px] font-semibold text-rose-600 hover:underline" data-testid="export-cols-clear">Hiçbiri</button>
                    <span className="ml-auto text-[11px] text-slate-500">Çıktıda seçili sütunlar yer alır (sıra sabit)</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded p-2">
                    {COLUMN_META.map((c) => (
                      <label key={c.k} className="flex items-center gap-2 text-xs text-slate-700 hover:bg-slate-50 px-1.5 py-1 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedCols.has(c.k)}
                          onChange={() => toggleCol(c.k)}
                          data-testid={`export-col-${c.k}`}
                        />
                        <span className="flex-1 truncate" title={c.l}>{c.l}</span>
                        <span className="text-[9px] text-slate-400">{c.t}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Column-filter inheritance notice */}
          {scope === "filtered" && activeColFilterCount > 0 && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-md p-2.5 text-[11px] text-indigo-900 flex items-start gap-2" data-testid="export-colfilter-notice">
              <Filter size={12} className="mt-0.5 shrink-0" />
              <span>Listede uyguladığınız <strong>{activeColFilterCount} sütun filtresi</strong> dışa aktarıma da uygulanacak.</span>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold hover:bg-white">İptal</button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            data-testid="export-download-btn"
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-lg text-sm font-semibold inline-flex items-center gap-1.5"
          >
            <Download size={14} /> {downloading ? "İndiriliyor..." : "İndir"}
          </button>
        </div>
      </div>
    </div>
  );
}


function ScopeOption({ checked, disabled, onClick, title, desc, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`text-left border rounded-md px-3 py-2 flex items-start gap-2.5 transition-colors ${
        checked ? "bg-emerald-50 border-emerald-400 ring-2 ring-emerald-200" : "bg-white border-slate-200 hover:border-slate-400"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <div className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 ${checked ? "bg-emerald-600 border-emerald-600" : "border-slate-300"}`}>
        {checked && <div className="w-full h-full rounded-full border-2 border-white"></div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${checked ? "text-emerald-900" : "text-slate-900"}`}>{title}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{desc}</div>
      </div>
    </button>
  );
}


function BulkEditModal({ selectedIds, creditors, onClose, onSaved }) {
  const [muvekkil, setMuvekkil] = useState("");
  const [tahsilatTarihi, setTahsilatTarihi] = useState("");
  const [gondermeTarihi, setGondermeTarihi] = useState("");
  const [saving, setSaving] = useState(false);

  const touched = (muvekkil ? 1 : 0) + (tahsilatTarihi ? 1 : 0) + (gondermeTarihi ? 1 : 0);

  const submit = async () => {
    if (touched === 0) {
      toast.error("Düzenlenecek en az bir alan seçin");
      return;
    }
    const patch = {};
    if (muvekkil) patch.muvekkil = muvekkil;
    if (tahsilatTarihi) patch.tahsilat_tarihi = tahsilatTarihi;
    if (gondermeTarihi) patch.gonderme_tarihi = gondermeTarihi;

    if (!window.confirm(`${selectedIds.length} tahsilat satırında ${touched} alan güncellenecek. Onaylıyor musunuz?`)) return;
    setSaving(true);
    try {
      const r = await api.post("/tahsilat/bulk-edit", { ids: selectedIds, patch });
      toast.success(`${r.data.updated} satır güncellendi`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toplu düzenleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onMouseDown={(e) => { e.currentTarget.dataset.down = e.target === e.currentTarget ? "1" : ""; }}
      onClick={(e) => { if (e.target === e.currentTarget && e.currentTarget.dataset.down === "1") onClose(); }}
      data-testid="tahsilat-bulk-edit-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Edit size={16} className="text-amber-600" />
            <h3 className="text-sm font-bold text-slate-900">Toplu Düzenle · {selectedIds.length} satır</h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={14} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-900 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <div>Yalnızca <strong>doldurduğunuz</strong> alanlar güncellenecek. Boş bıraktıklarınız dokunulmaz. Diğer alanlar (tutarlar, dosya no, vb.) değişmez.</div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">Müvekkil (değiştir)</label>
            <select
              value={muvekkil}
              onChange={(e) => setMuvekkil(e.target.value)}
              data-testid="bulk-edit-muvekkil"
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
            >
              <option value="">— Değiştirme —</option>
              {creditors.map((c) => <option key={c.id} value={c.ad}>{c.ad}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">Tahsilat Tarihi (değiştir)</label>
            <DateInput value={tahsilatTarihi} onChange={(e) => setTahsilatTarihi(e.target.value)} data-testid="bulk-edit-tahsilat-tarihi" className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">Gönderme Tarihi (değiştir)</label>
            <DateInput value={gondermeTarihi} onChange={(e) => setGondermeTarihi(e.target.value)} data-testid="bulk-edit-gonderme-tarihi" className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          </div>
          {touched > 0 && (
            <div className="text-xs text-emerald-700 font-semibold">
              {touched} alan değişecek · {selectedIds.length} satırda uygulanacak
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold hover:bg-white">İptal</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || touched === 0}
            data-testid="bulk-edit-save-btn"
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-lg text-sm font-semibold"
          >
            {saving ? "Güncelleniyor..." : `Güncelle (${selectedIds.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}

